import { FC } from 'react'
import { useFormik } from 'formik'
import {
  Button,
  FetchState,
  FormTextArea,
  LoadingIndicator,
  useModal,
  useToast,
} from '../../../../../../components'
import { AnotacaoType, ButtonType, ID, showError } from '../../../../../../../_metronic/helpers'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import { resolvePendencia } from '../../../../atendimento/core/_requests'

type Props = {
  atendimentoId: ID
}

/**
 * Pendência vista pelo solicitante: lê o que o teleconsultor pediu (anotações tipo PENDENCIA,
 * mais recente primeiro) e resolve, com observação opcional — que vira mais uma anotação de
 * pendência, visível ao teleconsultor. Espera o atendimento no fetcher `atendimento`.
 */
export const ResolverPendenciaModal: FC<Props> = ({ atendimentoId }) => {
  const { closeModal, fetchStates } = useModal()
  const { showToast } = useToast()
  const {
    data: atendimento,
    isLoading,
    error,
  } = (fetchStates['atendimento'] || {}) as FetchState<AtendimentoView>
  // Sem o atendimento carregado não dá para ler o que foi pedido: não deixa resolver às cegas.
  const carregado = !!atendimento && !error

  const pendencias = (atendimento?.anotacoes ?? [])
    .filter((anotacao) => anotacao.tipo === AnotacaoType.Pendencia)
    .sort((a, b) => (b.data ?? '').localeCompare(a.data ?? ''))

  const formik = useFormik({
    initialValues: { observacao: '' },
    onSubmit: async ({ observacao }) => {
      try {
        await resolvePendencia(observacao.trim(), atendimentoId)
        showToast({
          variant: 'success',
          title: 'Pendência resolvida',
          description: 'A interconsulta voltou para o teleconsultor.',
        })
        closeModal()
      } catch (error) {
        showError(error)
      }
    },
  })

  if (isLoading) return <LoadingIndicator />

  if (!carregado)
    return (
      <div className='d-flex flex-column gap-5'>
        <p role='alert' className='text-gray-900 m-0'>
          Não foi possível carregar a pendência. Feche e tente novamente.
        </p>
        <div className='d-flex justify-content-end'>
          <Button className='btn-secondary' onClick={closeModal}>
            Fechar
          </Button>
        </div>
      </div>
    )

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <section aria-labelledby='pendencia-titulo' className='mb-6'>
        <h3 id='pendencia-titulo' className='fs-6 fw-bold text-gray-900 mb-3'>
          O que o teleconsultor pediu
        </h3>
        {pendencias.length ? (
          <ul className='list-unstyled d-flex flex-column gap-3 m-0'>
            {pendencias.map((pendencia) => (
              <li key={pendencia.id} className='border border-warning rounded p-4 bg-light-warning'>
                <div className='fs-7 text-gray-700 mb-1'>
                  {pendencia.dataFormatada} · {pendencia.usuario?.nome}
                </div>
                <div className='text-gray-900' style={{ whiteSpace: 'pre-wrap' }}>
                  {pendencia.anotacao}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-gray-700 m-0'>Nenhum texto de pendência registrado.</p>
        )}
      </section>
      <FormTextArea
        formik={formik}
        name='observacao'
        label='Observação (opcional)'
        placeholder='Ex.: exame anexado; informações complementadas'
        rows={4}
      />
      <div className='d-flex justify-content-end gap-3 pt-6'>
        <Button className='btn-secondary' onClick={closeModal} disabled={formik.isSubmitting}>
          Cancelar
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          Resolver pendência
        </Button>
      </div>
    </form>
  )
}
